import { Button, Input, type Milestone, MilestoneTracker } from "@app/ui/next"
import { useQueryClient } from "@tanstack/react-query"
import { createRoute, useNavigate } from "@tanstack/react-router"
import {
	type CSSProperties,
	type ClipboardEvent,
	type FormEvent,
	type KeyboardEvent,
	type ReactNode,
	useEffect,
	useRef,
	useState,
} from "react"
import { requestOtp, verifyOtp } from "../api/otp"
import { sessionQueryKey } from "../auth/session"
import { Route as RootRoute } from "./__root"

/**
 * `/sign-in` -- OTP entry reframed as a tracked consignment.
 *
 * Left "manifest" panel (dark) is decorative branding: it renders the
 * one-time code as a shipment moving through five milestones while the
 * email is dispatched. Right panel is the real form.
 *
 * Step 1 (`email`/`transit`): work email + a "Dispatch one-time code"
 * button. Submitting fires the REAL `requestOtp(email, "email")` and,
 * concurrently, plays the manifest animation. Only when BOTH the request
 * resolves AND the animation finishes do we advance to verify; a rejected
 * request returns to the email step with an inline error. The five
 * milestones are purely cosmetic (they brand the wait) — they are NOT
 * backend states.
 *
 * Step 2 (`verify`): six single-character OTP cells (auto-advance,
 * Backspace-to-previous, paste-6-digits) + "Verify & board"
 * (`verifyOtp`). On success we invalidate the session query and await the
 * refetch BEFORE navigating home — navigating first makes `__root` read a
 * stale `null` session and bounce back here. A "Resend code" button locks
 * for 30s with a countdown; "Change email" returns to step 1.
 *
 * Bespoke full-bleed two-panel layout is inline-styled against the RAM-OS
 * token layer (same precedent as the dashboard's inline LIVE row); the
 * design-system primitives (`Button`, `Input`, `MilestoneTracker`,
 * `RefId`) are used at their real APIs. Code-based route to match the
 * scaffold in `routes/index.tsx`.
 */
export const Route = createRoute({
	getParentRoute: () => RootRoute,
	path: "/sign-in",
	component: SignInPage,
})

type Phase = "email" | "transit" | "verify"

const CODE_LENGTH = 6
const RESEND_SECONDS = 30
/** Per-milestone dwell for the decorative dispatch animation. */
const STEP_MS = 680
const CELLS = [0, 1, 2, 3, 4, 5] as const

const MILESTONE_DEFS: ReadonlyArray<{ label: string; location: string; code?: string }> = [
	{ label: "Code generated", location: "Secure enclave", code: "AE·KMS" },
	{ label: "Encrypted", location: "AES-256-GCM" },
	{ label: "Dispatched to relay", location: "Mail gateway", code: "SMTP" },
	{ label: "In transit", location: "En route to inbox" },
	{ label: "Delivered to inbox", location: "Consignee" },
]

const HERO: Record<Phase, { title: string; sub: string }> = {
	email: {
		title: "Your code, ready to dispatch.",
		sub: "Enter your email and we'll book a one-time code for delivery — then watch it move through every checkpoint to your inbox, live.",
	},
	transit: {
		title: "Your code is in transit.",
		sub: "Securing, dispatching and routing your code now. It clears each checkpoint and lands in your inbox in seconds.",
	},
	verify: {
		title: "Delivered to your inbox.",
		sub: "Code delivered. Enter the six digits to verify your consignment and board the console.",
	},
}

const STATUS: Record<Phase, { label: string; fg: string; bg: string; dot: string }> = {
	email: {
		label: "Awaiting dispatch",
		fg: "var(--neutral-300)",
		bg: "#2A2B2D",
		dot: "var(--text-faint)",
	},
	transit: { label: "In transit", fg: "var(--blue-500)", bg: "#1E2533", dot: "var(--blue-500)" },
	verify: { label: "Delivered", fg: "var(--green-400)", bg: "#16271E", dot: "var(--green-400)" },
}

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

const messageOf = (err: unknown, fallback: string): string =>
	err instanceof Error ? err.message : fallback

const formatClock = (d: Date): string =>
	[d.getHours(), d.getMinutes(), d.getSeconds()].map((n) => String(n).padStart(2, "0")).join(":")

const strokeIcon = (size: number, children: ReactNode): ReactNode => (
	<svg
		viewBox="0 0 24 24"
		width={size}
		height={size}
		fill="none"
		stroke="currentColor"
		strokeWidth={2}
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		{children}
	</svg>
)

const SEND_ICON = strokeIcon(
	16,
	<>
		<path d="M22 2 11 13" />
		<path d="M22 2 15 22l-4-9-9-4 20-7Z" />
	</>,
)
const ARROW_ICON = strokeIcon(
	16,
	<>
		<path d="M5 12h14" />
		<path d="m12 5 7 7-7 7" />
	</>,
)
const CHECK_ICON = strokeIcon(13, <path d="M20 6 9 17l-5-5" />)
const LOCK_ICON = strokeIcon(
	14,
	<>
		<path d="M5 11h14v10H5z" />
		<path d="M8 11V7a4 4 0 0 1 8 0v4" />
	</>,
)

const RAMOS_MARK = (
	<svg
		width="34"
		height="34"
		viewBox="0 0 40 40"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		role="img"
		aria-label="RAM-OS mark"
	>
		<rect
			x="0.6"
			y="0.6"
			width="38.8"
			height="38.8"
			rx="8.4"
			fill="#22242A"
			stroke="#F1EEEB"
			strokeOpacity="0.10"
			strokeWidth="1.2"
		/>
		<rect
			x="11"
			y="11"
			width="18"
			height="18"
			rx="5.5"
			fill="none"
			stroke="#F1EEEB"
			strokeWidth="2.4"
		/>
		<path d="M20 20 L26.5 13.5" stroke="#6FAB8D" strokeWidth="2" strokeLinecap="round" />
		<circle cx="20" cy="20" r="3.6" fill="#6FAB8D" />
	</svg>
)

// Decorative animated "shipping route" backdrop for the manifest panel: a
// faint map graticule, an animated dashed route with two packets traveling
// along it, pulsing waypoint rings and drifting particles. Purely cosmetic
// (the whole panel is aria-hidden); animations are killed under
// prefers-reduced-motion via the `.ros-*` classes in CSI_CSS.
const ROUTE_TEXTURE = (
	<svg
		width="100%"
		height="100%"
		viewBox="0 0 560 900"
		preserveAspectRatio="xMidYMid slice"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		style={{ position: "absolute", inset: 0 }}
		aria-hidden="true"
	>
		<defs>
			<path
				id="rosRoute"
				d="M -30 120 C 250 70 200 330 320 410 C 430 484 250 660 220 760 C 200 826 360 880 600 840"
			/>
			<radialGradient id="rosPacketG" cx="50%" cy="50%" r="50%">
				<stop offset="0%" stopColor="#9FD4BA" />
				<stop offset="55%" stopColor="#6FAB8D" />
				<stop offset="100%" stopColor="#6FAB8D" stopOpacity="0" />
			</radialGradient>
		</defs>
		{/* map graticule */}
		<g stroke="#F1EEEB" strokeOpacity="0.045" strokeWidth="1.2">
			<path d="M 90 -20 C 40 240 40 660 110 920" />
			<path d="M 270 -20 C 230 240 230 660 290 920" />
			<path d="M 450 -20 C 420 240 420 660 470 920" />
			<path d="M -20 200 C 200 160 380 160 580 210" />
			<path d="M -20 470 C 200 430 380 430 580 480" />
			<path d="M -20 720 C 200 680 380 680 580 730" />
		</g>
		{/* route base + animated dashes */}
		<use href="#rosRoute" stroke="#F1EEEB" strokeOpacity="0.08" strokeWidth="1.4" />
		<use
			className="ros-route-dash"
			href="#rosRoute"
			stroke="#6FAB8D"
			strokeOpacity="0.5"
			strokeWidth="1.6"
			strokeLinecap="round"
			strokeDasharray="2 12"
			style={{ animation: "rosDash 5s linear infinite" }}
		/>
		{/* waypoint nodes + pulse rings */}
		<circle
			className="ros-ring"
			cx="320"
			cy="410"
			r="8"
			fill="none"
			stroke="#F1EEEB"
			strokeOpacity="0.5"
			strokeWidth="1.2"
			style={{
				transformBox: "fill-box",
				transformOrigin: "center",
				animation: "rosRing 3.2s ease-out infinite",
			}}
		/>
		<circle
			className="ros-ring"
			cx="220"
			cy="760"
			r="8"
			fill="none"
			stroke="#F1EEEB"
			strokeOpacity="0.5"
			strokeWidth="1.2"
			style={{
				transformBox: "fill-box",
				transformOrigin: "center",
				animation: "rosRing 3.2s ease-out 1.1s infinite",
			}}
		/>
		<circle
			className="ros-node"
			cx="320"
			cy="410"
			r="3"
			fill="#F1EEEB"
			style={{ animation: "rosNode 3s ease-in-out infinite" }}
		/>
		<circle
			className="ros-node"
			cx="220"
			cy="760"
			r="3"
			fill="#F1EEEB"
			style={{ animation: "rosNode 3s ease-in-out .9s infinite" }}
		/>
		{/* origin pin */}
		<circle
			cx="-30"
			cy="120"
			r="4.5"
			fill="none"
			stroke="#6FAB8D"
			strokeOpacity="0.6"
			strokeWidth="1.4"
		/>
		{/* destination pin + radiating ring + orbiting marker */}
		<circle
			className="ros-ring"
			cx="600"
			cy="840"
			r="10"
			fill="none"
			stroke="#6FAB8D"
			strokeWidth="1.4"
			style={{
				transformBox: "fill-box",
				transformOrigin: "center",
				animation: "rosRing 2.6s ease-out infinite",
			}}
		/>
		<circle cx="600" cy="840" r="4" fill="#6FAB8D" />
		<g style={{ transformOrigin: "600px 840px", animation: "rosOrbit 9s linear infinite" }}>
			<circle cx="618" cy="840" r="2" fill="#9FD4BA" opacity="0.8" />
		</g>
		{/* traveling packet */}
		<g className="ros-packet">
			<circle r="14" fill="url(#rosPacketG)" opacity="0.85" />
			<circle r="3.4" fill="#CDEBDA" />
			<animateMotion
				dur="6s"
				repeatCount="indefinite"
				rotate="auto"
				calcMode="spline"
				keyTimes="0;1"
				keySplines="0.45 0 0.55 1"
			>
				<mpath href="#rosRoute" />
			</animateMotion>
		</g>
		{/* second trailing packet */}
		<g className="ros-packet" opacity="0.6">
			<circle r="8" fill="url(#rosPacketG)" opacity="0.7" />
			<circle r="2.2" fill="#CDEBDA" />
			<animateMotion
				dur="6s"
				begin="-2.6s"
				repeatCount="indefinite"
				rotate="auto"
				calcMode="spline"
				keyTimes="0;1"
				keySplines="0.45 0 0.55 1"
			>
				<mpath href="#rosRoute" />
			</animateMotion>
		</g>
		{/* ambient drifting particles */}
		<circle
			r="1.6"
			fill="#6FAB8D"
			cx="150"
			cy="300"
			style={{ animation: "rosDriftA 7s ease-in-out infinite" }}
		/>
		<circle
			r="1.3"
			fill="#F1EEEB"
			cx="430"
			cy="560"
			style={{ animation: "rosDriftB 8.5s ease-in-out 1.5s infinite" }}
		/>
		<circle
			r="1.5"
			fill="#6FAB8D"
			cx="360"
			cy="700"
			style={{ animation: "rosDriftA 9s ease-in-out 3s infinite" }}
		/>
		<circle
			r="1.2"
			fill="#F1EEEB"
			cx="120"
			cy="620"
			style={{ animation: "rosDriftB 7.5s ease-in-out .6s infinite" }}
		/>
	</svg>
)

// Scoped layout + the bits that need pseudo-selectors / media queries (the
// OTP focus ring and hiding the decorative manifest panel on small screens),
// which inline styles can't express. Injected once via a rendered <style>.
const CSI_CSS = `
.csi-page{display:flex;flex-direction:row;width:100%;min-height:100vh;background:var(--ink);font-family:var(--font-sans);}
.csi-manifest{flex:1.04;position:relative;overflow:hidden;background:var(--ink);display:flex;flex-direction:column;padding:44px 48px;color:var(--text-strong);min-width:0;}
.csi-form{flex:.96;background:var(--surface-card);display:flex;align-items:center;justify-content:center;padding:48px 40px;min-width:0;}
.csi-form-inner{width:100%;max-width:380px;}
.csi-fade{animation:csiFade .32s var(--ease-out) both;}
@keyframes csiFade{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
.csi-otp{width:100%;height:58px;text-align:center;font-family:var(--font-mono);font-size:24px;font-weight:500;font-variant-numeric:tabular-nums;color:var(--text-strong);background:var(--surface-card);border:1px solid var(--border-default);border-radius:var(--radius-md);box-shadow:var(--shadow-inset);transition:var(--transition-colors);}
.csi-otp::placeholder{color:var(--text-subtle);}
.csi-otp:focus{outline:none;border-color:var(--green-500);box-shadow:var(--ring),var(--shadow-inset);}
.csi-linkbtn{background:none;border:none;padding:0;cursor:pointer;font:var(--font-body-sm);font-weight:500;}
.csi-linkbtn:disabled{cursor:default;}
@keyframes rosEtaPulse{0%{transform:scale(1);opacity:.4;}70%{transform:scale(2.6);opacity:0;}100%{opacity:0;}}
@keyframes rosDash{to{stroke-dashoffset:-340;}}
@keyframes rosNode{0%,100%{opacity:.25;}50%{opacity:.7;}}
@keyframes rosRing{0%{transform:scale(.6);opacity:.55;}80%,100%{transform:scale(2.4);opacity:0;}}
@keyframes rosDriftA{0%{transform:translate(0,0);opacity:0;}15%,85%{opacity:.5;}100%{transform:translate(26px,-60px);opacity:0;}}
@keyframes rosDriftB{0%{transform:translate(0,0);opacity:0;}20%,80%{opacity:.4;}100%{transform:translate(-20px,-48px);opacity:0;}}
@keyframes rosOrbit{to{transform:rotate(360deg);}}
@media (prefers-reduced-motion:reduce){
	.ros-route-dash,.ros-packet,.ros-ring,.ros-node,.ros-eta-ping,.csi-manifest svg [style*="rosDrift"],.csi-manifest svg [style*="rosOrbit"]{animation:none!important;}
}
@media (max-width:860px){
	.csi-manifest{display:none;}
	.csi-form{flex:1;background:var(--surface-card);}
}
`

const TEXTURE_WRAP: CSSProperties = {
	position: "absolute",
	inset: 0,
	pointerEvents: "none",
	overflow: "hidden",
}
const BRAND_LOCKUP: CSSProperties = {
	position: "relative",
	display: "flex",
	alignItems: "center",
	gap: 11,
}
const WORDMARK: CSSProperties = {
	fontSize: 16,
	fontWeight: 600,
	letterSpacing: ".4px",
	color: "var(--paper)",
}
const HERO_WRAP: CSSProperties = {
	position: "relative",
	flex: 1,
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	maxWidth: 430,
}
const EYEBROW: CSSProperties = {
	font: "var(--weight-semibold) 11px/1 var(--font-sans)",
	letterSpacing: "var(--tracking-label)",
	textTransform: "uppercase",
	color: "var(--green-400)",
	marginBottom: 14,
}
const HERO_TITLE: CSSProperties = {
	margin: "0 0 10px",
	fontSize: 27,
	lineHeight: 1.2,
	fontWeight: 600,
	letterSpacing: "-.01em",
	color: "var(--paper)",
}
const HERO_SUB: CSSProperties = {
	margin: "0 0 26px",
	fontSize: 14,
	lineHeight: 1.55,
	color: "var(--text-muted)",
	maxWidth: 360,
}
const CARD: CSSProperties = {
	background: "var(--surface-card)",
	border: "1px solid var(--border-default)",
	borderRadius: "var(--radius-lg)",
	boxShadow: "var(--shadow-lg)",
	overflow: "hidden",
}
const CARD_HEAD: CSSProperties = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	padding: "13px 16px",
	borderBottom: "1px solid var(--border-subtle)",
}
const CARD_HEAD_LABEL: CSSProperties = {
	font: "var(--weight-semibold) 10px/1 var(--font-sans)",
	letterSpacing: "var(--tracking-label)",
	textTransform: "uppercase",
	color: "var(--text-muted)",
}
const CARD_FOOT: CSSProperties = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: 12,
	padding: "13px 16px",
	borderTop: "1px solid var(--border-subtle)",
	background: "var(--surface-2)",
}
const PROGRESS: CSSProperties = {
	font: "var(--weight-medium) var(--text-xs)/1 var(--font-mono)",
	fontVariantNumeric: "tabular-nums",
	color: "var(--text-muted)",
	flex: "none",
}
// "Live ETA" indicator shown in the consignment-card footer (replaces the
// booking RefId): a pulsing phase-coloured dot, an uppercase label and the
// live countdown / arrival value.
const ETA_WRAP: CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 9,
	minWidth: 0,
}
const ETA_DOT: CSSProperties = {
	position: "relative",
	display: "inline-flex",
	width: 7,
	height: 7,
	flex: "none",
}
const ETA_PING: CSSProperties = {
	position: "absolute",
	inset: 0,
	borderRadius: "50%",
	opacity: 0.4,
	animation: "rosEtaPulse 1.6s ease-out infinite",
}
const ETA_CORE: CSSProperties = {
	position: "absolute",
	inset: 1,
	borderRadius: "50%",
}
const ETA_LABEL: CSSProperties = {
	font: "var(--weight-semibold) 9px/1 var(--font-sans)",
	letterSpacing: "var(--tracking-label)",
	textTransform: "uppercase",
	color: "var(--text-faint)",
	flex: "none",
}
const ETA_VALUE: CSSProperties = {
	font: "var(--weight-medium) 13px/1 var(--font-mono)",
	letterSpacing: ".04em",
	fontVariantNumeric: "tabular-nums",
	color: "var(--text-body)",
	whiteSpace: "nowrap",
}
const H2: CSSProperties = {
	margin: "0 0 6px",
	font: "var(--font-page-title)",
	color: "var(--text-strong)",
}
const LEAD: CSSProperties = {
	margin: "0 0 22px",
	fontSize: 14,
	lineHeight: 1.55,
	color: "var(--text-muted)",
}
const STRONG: CSSProperties = { color: "var(--text-body)", fontWeight: 600 }
const LOCK_NOTE: CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 7,
	marginTop: 18,
	fontSize: 12,
	color: "var(--text-faint)",
}
const DELIVERED_PILL: CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	gap: 7,
	font: "var(--weight-semibold) 10px/1 var(--font-sans)",
	letterSpacing: "var(--tracking-label)",
	textTransform: "uppercase",
	color: "var(--green-700)",
	background: "var(--green-50)",
	borderRadius: "var(--radius-pill)",
	padding: "5px 10px",
	marginBottom: 16,
}
const MONO_EMAIL: CSSProperties = {
	color: "var(--text-body)",
	fontFamily: "var(--font-mono)",
	fontSize: 13,
}
const CELLS_ROW: CSSProperties = { display: "flex", gap: 9, marginBottom: 10 }
const EXPIRY_NOTE: CSSProperties = {
	font: "var(--weight-medium) 11px/1 var(--font-mono)",
	color: "var(--text-faint)",
	marginBottom: 20,
}
const ERROR_TEXT: CSSProperties = {
	margin: "0 0 16px",
	fontSize: 13,
	color: "var(--red-600)",
}
const VERIFY_ACTIONS: CSSProperties = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	marginTop: 18,
}
const FIELD_WRAP: CSSProperties = { marginBottom: 18 }

const statusPillStyle = (phase: Phase): CSSProperties => ({
	display: "inline-flex",
	alignItems: "center",
	gap: 6,
	fontSize: 11,
	fontWeight: 600,
	borderRadius: "var(--radius-pill)",
	padding: "3px 9px 3px 8px",
	whiteSpace: "nowrap",
	color: STATUS[phase].fg,
	background: STATUS[phase].bg,
})

export function SignInPage() {
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const [phase, setPhase] = useState<Phase>("email")
	const [email, setEmail] = useState("")
	const [otp, setOtp] = useState<string[]>(() => Array.from({ length: CODE_LENGTH }, () => ""))
	const [step, setStep] = useState(0)
	const [times, setTimes] = useState<string[]>([])
	const [resendIn, setResendIn] = useState(0)
	const [resending, setResending] = useState(false)
	const [verifying, setVerifying] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [bookingRef] = useState(() => `RMX-2025-${Math.floor(1000 + Math.random() * 9000)}`)

	const cellRefs = useRef<Array<HTMLInputElement | null>>([])
	const animRef = useRef<ReturnType<typeof setInterval> | null>(null)

	const clearAnim = () => {
		if (animRef.current !== null) {
			clearInterval(animRef.current)
			animRef.current = null
		}
	}

	// Clear the dispatch interval on unmount.
	useEffect(() => {
		return () => {
			if (animRef.current !== null) clearInterval(animRef.current)
		}
	}, [])

	// Resend lock countdown.
	useEffect(() => {
		if (resendIn <= 0) return
		const id = setTimeout(() => setResendIn((n) => n - 1), 1000)
		return () => clearTimeout(id)
	}, [resendIn])

	// Land focus on the first OTP cell when the verify step appears.
	useEffect(() => {
		if (phase === "verify") cellRefs.current[0]?.focus()
	}, [phase])

	// Decorative dispatch animation: steps the manifest through its five
	// milestones, resolving once the consignment is "delivered".
	const runManifestAnimation = (): Promise<void> =>
		new Promise<void>((resolve) => {
			clearAnim()
			setStep(0)
			setTimes([])
			let current = 0
			animRef.current = setInterval(() => {
				current += 1
				const stamp = formatClock(new Date())
				setStep(current)
				setTimes((prev) => {
					const next = prev.slice()
					next[current - 1] = stamp
					return next
				})
				if (current >= MILESTONE_DEFS.length) {
					clearAnim()
					resolve()
				}
			}, STEP_MS)
		})

	const handleDispatch = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		if (phase === "transit" || !isValidEmail(email)) return
		setError(null)
		setPhase("transit")
		// The email is genuinely sent here; the animation only brands the wait.
		Promise.all([requestOtp(email, "email"), runManifestAnimation()]).then(
			() => {
				setPhase("verify")
				setOtp(Array.from({ length: CODE_LENGTH }, () => ""))
				setResendIn(RESEND_SECONDS)
			},
			(err: unknown) => {
				clearAnim()
				setPhase("email")
				setError(messageOf(err, "Couldn't dispatch the code. Try again."))
			},
		)
	}

	const handleVerify = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const code = otp.join("")
		if (verifying || code.length < CODE_LENGTH) return
		setVerifying(true)
		setError(null)
		verifyOtp(email, code).then(
			async () => {
				// Invalidate + await the session refetch BEFORE navigating so
				// `__root` reads the freshly-minted session at "/" instead of the
				// stale `null` it would otherwise bounce us back to /sign-in on.
				await queryClient.invalidateQueries({ queryKey: sessionQueryKey })
				void navigate({ to: "/" })
			},
			(err: unknown) => {
				setError(messageOf(err, "That code is invalid or expired."))
				setVerifying(false)
			},
		)
	}

	const handleResend = () => {
		if (resending || resendIn > 0) return
		setResending(true)
		setError(null)
		requestOtp(email, "email").then(
			() => {
				setResendIn(RESEND_SECONDS)
				setResending(false)
			},
			(err: unknown) => {
				setError(messageOf(err, "Couldn't resend the code. Try again."))
				setResending(false)
			},
		)
	}

	const handleBack = () => {
		clearAnim()
		setPhase("email")
		setOtp(Array.from({ length: CODE_LENGTH }, () => ""))
		setStep(0)
		setTimes([])
		setResendIn(0)
		setResending(false)
		setVerifying(false)
		setError(null)
	}

	const handleOtpChange = (index: number, raw: string) => {
		const digit = raw.replace(/\D/g, "").slice(-1)
		setOtp((prev) => {
			const next = prev.slice()
			next[index] = digit
			return next
		})
		if (digit) cellRefs.current[index + 1]?.focus()
	}

	const handleOtpKey = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace" && !otp[index]) {
			const prev = cellRefs.current[index - 1]
			if (prev) {
				prev.focus()
				setOtp((current) => {
					const next = current.slice()
					next[index - 1] = ""
					return next
				})
			}
		}
	}

	const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
		const digits = e.clipboardData
			.getData("text")
			.replace(/\D/g, "")
			.slice(0, CODE_LENGTH)
			.split("")
		if (digits.length === 0) return
		e.preventDefault()
		setOtp(Array.from({ length: CODE_LENGTH }, (_, i) => digits[i] ?? ""))
		cellRefs.current[Math.min(digits.length, CODE_LENGTH - 1)]?.focus()
	}

	const milestones: Milestone[] = MILESTONE_DEFS.map((def, i) => {
		let state: Milestone["state"]
		if (phase === "email") state = "upcoming"
		else if (i < step) state = "done"
		else if (i === step) state = "current"
		else state = "upcoming"
		const time = times[i]
		return {
			label: def.label,
			location: def.location,
			state,
			...(def.code ? { code: def.code } : {}),
			...(time ? { time } : {}),
		}
	})

	const hero = HERO[phase]

	// "Live ETA" footer: phase-coloured dot + countdown that shrinks as the
	// decorative consignment clears milestones, landing on "In your inbox".
	const etaColor =
		phase === "transit"
			? "var(--blue-500)"
			: phase === "verify"
				? "var(--green-400)"
				: "var(--text-faint)"
	const etaLabel = phase === "verify" ? "Arrived" : "Live ETA"
	const etaValue =
		phase === "transit"
			? `~${Math.max(1, (MILESTONE_DEFS.length - Math.min(step, MILESTONE_DEFS.length)) * 2)}s to inbox`
			: phase === "verify"
				? "In your inbox"
				: ""

	return (
		<>
			<style>{CSI_CSS}</style>
			<div className="csi-page">
				{/* ===== Manifest panel (decorative branding) ===== */}
				<section className="csi-manifest" data-gm-theme="dark" aria-hidden="true">
					<div style={TEXTURE_WRAP}>{ROUTE_TEXTURE}</div>

					<div style={BRAND_LOCKUP}>
						<span style={{ display: "inline-flex", flex: "none" }}>{RAMOS_MARK}</span>
						<span style={WORDMARK}>
							RAM<span style={{ color: "var(--green-400)" }}>·</span>OS
						</span>
					</div>

					<div style={HERO_WRAP}>
						<div style={EYEBROW}>One-time code · consignment</div>
						<h1 style={HERO_TITLE}>{hero.title}</h1>
						<p style={HERO_SUB}>{hero.sub}</p>

						<div style={CARD}>
							<div style={CARD_HEAD}>
								<span style={CARD_HEAD_LABEL}>Track &amp; trace</span>
								<span style={statusPillStyle(phase)}>
									<span
										style={{
											width: 6,
											height: 6,
											borderRadius: "50%",
											background: STATUS[phase].dot,
										}}
									/>
									{STATUS[phase].label}
								</span>
							</div>
							<div style={{ padding: "18px 16px 6px" }}>
								<MilestoneTracker milestones={milestones} orientation="vertical" />
							</div>
							<div style={CARD_FOOT}>
								<div style={ETA_WRAP}>
									<span style={ETA_DOT}>
										<span className="ros-eta-ping" style={{ ...ETA_PING, background: etaColor }} />
										<span style={{ ...ETA_CORE, background: etaColor }} />
									</span>
									<span style={ETA_LABEL}>{etaLabel}</span>
									{etaValue ? <span style={ETA_VALUE}>{etaValue}</span> : null}
								</div>
								<span style={PROGRESS}>
									{Math.min(step, MILESTONE_DEFS.length)} / {MILESTONE_DEFS.length} milestones
								</span>
							</div>
						</div>
					</div>
				</section>

				{/* ===== Form panel (the real OTP flow) ===== */}
				<section className="csi-form">
					<div className="csi-form-inner">
						{phase !== "verify" ? (
							<form className="csi-fade" onSubmit={handleDispatch}>
								<h2 style={H2}>Sign in to RAM-OS</h2>
								<p style={LEAD}>
									Enter your work email. We'll <strong style={STRONG}>dispatch</strong> a one-time
									code and you can track it landing in your inbox.
								</p>
								<div style={FIELD_WRAP}>
									<Input
										label="Work email"
										type="email"
										inputMode="email"
										autoComplete="email"
										placeholder="you@company.com"
										size="lg"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										disabled={phase === "transit"}
										autoFocus
									/>
								</div>
								{error !== null ? <p style={ERROR_TEXT}>{error}</p> : null}
								<Button
									type="submit"
									variant="primary"
									size="lg"
									fullWidth
									loading={phase === "transit"}
									disabled={phase === "transit" || !isValidEmail(email)}
									rightIcon={SEND_ICON}
								>
									{phase === "transit" ? "Dispatching…" : "Dispatch one-time code"}
								</Button>
								<div style={LOCK_NOTE}>
									<span style={{ display: "inline-flex", color: "var(--text-muted)" }}>
										{LOCK_ICON}
									</span>
									Encrypted end-to-end · no passwords stored
								</div>
							</form>
						) : (
							<form className="csi-fade" onSubmit={handleVerify}>
								<div style={DELIVERED_PILL}>
									<span style={{ display: "inline-flex" }}>{CHECK_ICON}</span>
									Delivered to inbox
								</div>
								<h2 style={H2}>Enter the code</h2>
								<p style={LEAD}>
									6-digit code dispatched to <span style={MONO_EMAIL}>{email}</span>
								</p>

								<div style={CELLS_ROW} onPaste={handlePaste}>
									{CELLS.map((i) => (
										<input
											key={i}
											ref={(el) => {
												cellRefs.current[i] = el
											}}
											className="csi-otp"
											value={otp[i] ?? ""}
											onChange={(e) => handleOtpChange(i, e.target.value)}
											onKeyDown={(e) => handleOtpKey(i, e)}
											inputMode="numeric"
											maxLength={1}
											placeholder="•"
											aria-label={`Verification digit ${i + 1}`}
											autoComplete={i === 0 ? "one-time-code" : "off"}
										/>
									))}
								</div>
								<div style={EXPIRY_NOTE}>Booking {bookingRef} · expires in 10 minutes</div>

								{error !== null ? <p style={ERROR_TEXT}>{error}</p> : null}

								<Button
									type="submit"
									variant="primary"
									size="lg"
									fullWidth
									loading={verifying}
									disabled={verifying || otp.some((c) => c === "")}
									rightIcon={ARROW_ICON}
								>
									Verify &amp; board
								</Button>

								<div style={VERIFY_ACTIONS}>
									<button
										type="button"
										className="csi-linkbtn"
										onClick={handleResend}
										disabled={resending || resendIn > 0}
										style={{ color: resendIn > 0 ? "var(--text-faint)" : "var(--green-700)" }}
									>
										{resendIn > 0 ? `Resend code (${resendIn}s)` : "Resend code"}
									</button>
									<button
										type="button"
										className="csi-linkbtn"
										onClick={handleBack}
										style={{ color: "var(--text-muted)" }}
									>
										Change email
									</button>
								</div>
							</form>
						)}
					</div>
				</section>
			</div>
		</>
	)
}
